/**
 * @flow
 * Auth Sagas
 */
import { all, call, fork, put, takeEvery } from "redux-saga/effects";

import { LOGIN_USER, LOGOUT_USER, CREATE_NEW_PASSWORD } from "Actions/types";

import {
  signinUserSuccess,
  signinUserFailure,
  logoutUserFromCognitoFailure,
  logoutUserFromCognitoSuccess,
  getNewPassword
} from "Actions";
import * as Sentry from "@sentry/browser";
import Amplify, { Auth } from "aws-amplify";
import { errorHandling } from "Helpers/helpers";
import config from "Config";

async function getAWSConfigs(_config) {
  try {
    let result = await fetch("/config.json", {
      method: "GET",
      mode: "cors"
    });

    result = await result.json();
    console.log("GET AWS _CONFIGS", result);
    _config.cognito.userpool_id = result.userpool_id;
    _config.cognito.client_id = result.client_id;
    _config.api.endpoint = result.api_endpoint;
    const AppSyncCred = {
      //  aws_appsync_graphqlEndpoint: config.appsync.endpoint,
      aws_appsync_region: _config.aws.region,
      aws_appsync_authenticationType: "AMAZON_COGNITO_USER_POOLS",
      aws_cognito_region: _config.aws.region,
      aws_project_name: "ConnectShip-Manager",
      aws_project_region: _config.aws.region,
      aws_user_pools_id: _config.cognito.userpool_id,
      aws_user_pools_web_client_id: _config.cognito.client_id
    };
    Amplify.configure(AppSyncCred);
  } catch (error) {
    errorHandling(error);
  }
}
getAWSConfigs(config);

/**
 * Sigin User With Email and Password Request
 */
const signInUserWithEmailPasswordRequest = async (
  email: string,
  password: string
): Object => {
  Sentry.addBreadcrumb({
    message: "Attempted Login",
    data: { email: email }
  });
  return await Auth.signIn(email, password)
    .then(
      (authUser: Object): Object => {
        return authUser;
      }
    )
    .catch(
      (error: Error): Error => {
        console.log("FAILED TO LOGIN", error);
        if (error.message) {
          errorHandling(new Error(error.message));
        }
        return error;
      }
    );
};
let hasToken = false;
for (let i = 0; i < localStorage.length; i++) {
  if (localStorage.key(i).includes("idToken")) {
    hasToken = true;
  }
  if (i === localStorage.length - 1 && hasToken === false) {
    localStorage.clear();
  }
}
/**
 * Signout Request
 */
const signOutRequest = async (): Object =>
  await Auth.signOut()
    .then((result: Object): Object => result)
    .catch(
      (error: Error): Error => {
        errorHandling(error);
        return error;
      }
    );

/**
 * Signin User With Email & Password
 * User Auth: Start
 * This method handles the response from the API,
 * the request is sent through signInUserWithEmailPasswordRequest
 * and on the response of API is handled through here.
 */
function* signInUserWithEmailPassword({ payload }: Object): Generator {
  const { email, password, intl } = payload.user;
  const { history } = payload;
  try {
    const signInUser = yield call(
      signInUserWithEmailPasswordRequest,
      email,
      password
    );

    if (signInUser.challengeName === "NEW_PASSWORD_REQUIRED") {
      // If this is the first time user is logging in and we need to reset their password
      yield put(
        getNewPassword({
          email: email,
          tempPassword: password
        })
      );
      history.push("/starting");
    } else {
      if (signInUser.message) {
        yield put(signinUserFailure(signInUser.message));
      } else {
        Sentry.configureScope((scope: Object) => {
          scope.setUser({
            email: email
          });
        });

        localStorage.setItem("user_id", signInUser.username);
        console.log("SIGN IN USER", signInUser);
        yield put(signinUserSuccess(signInUser));
        history.push("/");
      }
    }
  } catch (error) {
    errorHandling(error);
    yield put(signinUserFailure(error.message));
    history.push("/signin");
  }
}

/**
 * Signs out the user
 *
 * @param {Object} { payload }
 * @returns {Object}
 */
function* signOut({ payload }: Object): Object {
  const { history } = payload;

  try {
    yield call(signOutRequest);
    localStorage.removeItem("user_id");
    localStorage.removeItem("user_groups");
    localStorage.removeItem("user_email");
    Sentry.configureScope((scope: Object): Object => scope.setUser(null));
    yield put(logoutUserFromCognitoSuccess());
    history.push("/signin");
  } catch (error) {
    errorHandling(error);
    yield put(logoutUserFromCognitoFailure(error.message));
    history.push("/signin");
  }
}
function* createNewPassword({ payload }: Object): Generator {
  const { email, tempPassword, passwordConfirmation } = payload.user;
  const password = passwordConfirmation;
  const { history } = payload;
  try {
    const newUser = yield call(
      signInUserWithEmailPasswordRequest,
      email,
      tempPassword
    );
    if (newUser.challengeName === "NEW_PASSWORD_REQUIRED") {
      const signInUser = yield call(
        createNewUserPasswordRequest,
        newUser,
        password
      );
      Sentry.configureScope((scope: Object) => {
        scope.setUser({
          email: email
        });
      });

      localStorage.setItem("user_id", signInUser.username);
      console.log("SIGN IN USER", signInUser);
      yield put(signinUserSuccess(signInUser));
      history.push("/");
    }
  } catch (error) {
    errorHandling(error);
    yield put(signinUserFailure(error.message));
    history.push("/signin");
  }
}
const createNewUserPasswordRequest = async (
  user: Object,
  password: String
): Object =>
  await Auth.completeNewPassword(user, password)
    .then((authUser: Object): Object => authUser)
    .catch(
      (error: Error): Error => {
        errorHandling(error);
        return error;
      }
    );

export function* signinUserInCognito(): Generator {
  yield takeEvery(LOGIN_USER, signInUserWithEmailPassword);
}
export function* createNewUserPassword(): Generator {
  yield takeEvery(CREATE_NEW_PASSWORD, createNewPassword);
}

/**
 * Signout User From Firebase
 */
export function* signOutUser(): Generator {
  yield takeEvery(LOGOUT_USER, signOut);
}

/**
 * Auth Root Saga
 */
export default function* rootSaga(): Generator {
  yield all([fork(signinUserInCognito), fork(signOutUser),fork(createNewUserPassword)]);
}
